import {
  Banner,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Icon,
  ListSkeleton,
  Page,
  PageHeader,
  SkeletonRegion,
  UiLink,
} from '@nexus/ui';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n/format';
import { safePath } from '../lib/paths';
import { noteText, useReleaseNotes, type ReleaseNote } from './release-notes';

function Note({ note }: { note: ReleaseNote }) {
  const { t, i18n } = useTranslation();
  const text = noteText(note, i18n.language);
  const link = note.link && safePath(note.link);
  return (
    <Card id={note.id}>
      <CardHeader
        title={text.title}
        description={
          <time dateTime={note.date}>{formatDate(new Date(note.date), i18n.language)}</time>
        }
      />
      <CardContent className="flex flex-col gap-3">
        <p className="text-fg">{text.text}</p>
        {text.howTo && (
          <Banner tone="info" icon="lightbulb" title={t('whatsNew.howTo')}>
            {text.howTo}
          </Banner>
        )}
        {link && (
          <UiLink
            href={link}
            className="inline-flex items-center gap-1.5 self-start font-medium text-brand-text hover:underline"
          >
            {t('whatsNew.tryIt')}
            <Icon name="arrow-right" className="size-4" />
          </UiLink>
        )}
      </CardContent>
    </Card>
  );
}

function Notes() {
  const { t } = useTranslation();
  const notes = useReleaseNotes();
  if (notes.length === 0) {
    return (
      <EmptyState
        icon="megaphone"
        title={t('whatsNew.emptyTitle')}
        description={t('whatsNew.emptyDescription')}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {notes.map((note) => (
        <Note key={note.id} note={note} />
      ))}
    </div>
  );
}

/**
 * "Was ist neu": changes of the app for users, from the commit messages, newest first
 * (docs/architecture/whats-new.md).
 */
export function WhatsNewPage() {
  const { t } = useTranslation();
  return (
    <Page>
      <PageHeader title={t('whatsNew.title')} description={t('whatsNew.description')} />
      <Suspense
        fallback={
          <SkeletonRegion>
            <ListSkeleton rows={3} />
          </SkeletonRegion>
        }
      >
        <Notes />
      </Suspense>
    </Page>
  );
}
